"use client";

import React, { useEffect, useState } from "react";
import { Menu, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, NavigationMenuContent } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
const getHref = (label: string, originalHref: string | undefined) => {
  const lowerLabel = label.toLowerCase();
  if (lowerLabel === "home") return "/";
  if (lowerLabel === "the story") return "/the-story/";
  if (lowerLabel === "our journeys") return "/our-journeys/";
  if (lowerLabel === "explorer" || lowerLabel === "explorer series") return "/explorer-series/";
  if (lowerLabel === "booking" || lowerLabel === "reserve your seat") return "/reserve-your-seat/";
  if (lowerLabel === "blog") return "/blog/";
  if (lowerLabel === "why frontier") return "/why-frontier/";
  if (lowerLabel === "destinations") return "/destinations/";
  if (lowerLabel.includes("cao bang loop 2 day")) return "/CaoBangLoop2DayTourPage";
  if (lowerLabel.includes("cao bang loop 3 day")) return "/CaoBangLoop3DayTourPage";
  return originalHref || "/";
};
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="234037" className="flex gap-4">
        <div className="h-9 w-20 animate-pulse rounded-md bg-muted" />
        <div className="h-9 w-20 animate-pulse rounded-md bg-muted" />
        <div className="h-9 w-20 animate-pulse rounded-md bg-muted" />
      </div>;
  }
  const hasContact = menuItems.some(item => item.label.toLowerCase().includes("contact"));
  return <NavigationMenu viewport={true}>
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent px-4 py-2 text-[13px] font-medium uppercase tracking-widest text-foreground transition-colors hover:bg-transparent hover:text-primary focus:bg-transparent focus:text-primary data-[state=open]:bg-transparent font-default">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[240px] gap-1 p-2">
                    {item.children.map((child, childIdx) => <li key={child.id} data-index={childIdx}>
                        <NavigationMenuLink asChild>
                          <Link to={getHref(child.label, child.href)} className="block rounded-md p-3 text-[12px] font-medium uppercase tracking-wider text-foreground transition-colors hover:bg-accent hover:text-primary font-default">
                            {child.label}
                          </Link>
                        </NavigationMenuLink>
                      </li>)}
                  </ul>
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={getHref(item.label, item.href)} className="group relative flex items-center justify-center bg-transparent px-4 py-2 text-[13px] font-medium uppercase tracking-widest text-foreground transition-colors hover:bg-transparent hover:text-primary focus:bg-transparent focus:text-primary data-[active=true]:bg-transparent data-[active=true]:text-primary font-default">
                  {item.label}
                  <span className="absolute bottom-1 left-1/2 h-[1px] w-0 -translate-x-1/2 bg-primary transition-all duration-300 ease-out group-hover:w-[calc(100%-2rem)]" />
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
        {!hasContact && <NavigationMenuItem>
            <NavigationMenuLink asChild>
              <Link to="/ContactPage" className="group relative flex items-center justify-center bg-transparent px-4 py-2 text-[13px] font-medium uppercase tracking-widest text-foreground transition-colors hover:bg-transparent hover:text-primary focus:bg-transparent focus:text-primary data-[active=true]:bg-transparent data-[active=true]:text-primary font-default">
                Contact
                <span className="absolute bottom-1 left-1/2 h-[1px] w-0 -translate-x-1/2 bg-primary transition-all duration-300 ease-out group-hover:w-[calc(100%-2rem)]" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="mt-8 flex flex-col gap-4 px-4">
        <div className="h-10 w-full animate-pulse rounded-md bg-muted" />
        <div className="h-10 w-full animate-pulse rounded-md bg-muted" />
        <div className="h-10 w-full animate-pulse rounded-md bg-muted" />
      </div>;
  }
  const hasContact = menuItems.some(item => item.label.toLowerCase().includes("contact"));
  return <div className="mt-8 flex flex-col gap-6 px-4">
      <nav className="flex flex-col gap-4">
        {menuItems.map((item, i) => <div key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <Collapsible className="w-full">
                <CollapsibleTrigger className="flex w-full items-center justify-between text-lg font-medium uppercase tracking-widest text-foreground transition-colors hover:text-primary font-default">
                  {item.label}
                  <ChevronDown className="size-5 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2 flex flex-col gap-3 pl-4 border-l border-border/50">
                  {item.children.map((child, childIdx) => <Link key={child.id} data-index={childIdx} to={getHref(child.label, child.href)} className="block text-sm font-medium uppercase tracking-wider text-muted-foreground transition-colors hover:text-primary font-default">
                      {child.label}
                    </Link>)}
                </CollapsibleContent>
              </Collapsible> : <Link to={getHref(item.label, item.href)} className="block text-lg font-medium uppercase tracking-widest text-foreground transition-colors hover:text-primary font-default">
                {item.label}
              </Link>}
          </div>)}
        {!hasContact && <div>
            <Link to="/ContactPage" className="block text-lg font-medium uppercase tracking-widest text-foreground transition-colors hover:text-primary font-default">
              Contact
            </Link>
          </div>}
      </nav>
      <div className="mt-4">
        <div className="group relative inline-flex w-full">
          <Button asChild className="relative w-full border border-foreground bg-transparent text-foreground hover:bg-foreground hover:text-background transition-all duration-300 font-default uppercase text-xs tracking-widest">
            <Link to="/ReserveYourSeatPage">Reserve Your Seat</Link>
          </Button>
        </div>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 ${isScrolled ? "border-b border-border/50 bg-background/80 backdrop-blur-md" : "border-b border-transparent bg-background"}`}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center">
          <WvcLogo className="h-6 w-auto" />
        </Link>
        
        <div className="hidden items-center gap-8 md:flex">
          <MenuProvider menu_id="46">
            <HeaderMenuConsumer />
          </MenuProvider>

          <div className="group relative inline-flex">
            <Button asChild className="relative border border-foreground/20 bg-transparent text-foreground hover:bg-foreground hover:text-background transition-all duration-300 font-default uppercase text-[11px] tracking-[0.2em] px-6">
              <Link to="/ReserveYourSeatPage">Reserve Your Seat</Link>
            </Button>
          </div>
        </div>

        <div className="md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground hover:bg-transparent">
                <Menu className="size-5" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] border-l border-border bg-background p-0 sm:w-[400px]">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <div className="flex h-20 items-center border-b border-border px-4">
                <WvcLogo className="h-6 w-auto" />
              </div>
              <MenuProvider menu_id="46">
                <MobileMenuConsumer />
              </MenuProvider>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}